import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Chip, PageHeader, PageLoading, Select, Switch, Textarea, useToast } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { adminKey, money, useAdminQuery } from '../../lib/queries.ts';
import './config-assistant.css';

type Scope = 'ai' | 'scheduling' | 'services' | 'hours';
const SCOPES: Scope[] = ['ai', 'scheduling', 'services', 'hours'];
type Faq = { question: string; answer: string };
interface AiPart { assistantName?: string; tone?: string; systemPrompt?: string; mustKnow?: string[]; mustNotDo?: string[]; faq?: Faq[]; outOfHoursMessage?: string; contingencyMessage?: string; handoffBehavior?: string; appointmentNotesRules?: string }
interface SchedulingPart { [k: string]: unknown }
interface ServicePart { id?: string; name: string; description?: string | null; durationMin?: number; price?: number | null; priceVisibility?: string; bufferBeforeMin?: number; bufferAfterMin?: number }
type DayHours = { open: boolean; intervals: { start: string; end: string }[]; lunch: { start: string; end: string } | null };
interface Proposal { ai?: AiPart; scheduling?: SchedulingPart; services?: ServicePart[]; hours?: Record<string, DayHours> }
interface Current { ai: AiPart; scheduling: SchedulingPart; services: ServicePart[]; hours: Record<string, DayHours> }
interface Session { id: string; businessId: string; scopes: Scope[]; model: string; status: 'OPEN' | 'APPLIED'; messages: { role: 'user' | 'assistant'; text: string; at: string }[]; proposal: Proposal | null; current: Current; cost: number; appliedParts: string[]; createdAt: string }
interface SessionRow { id: string; origin: 'ADMIN' | 'BUSINESS'; status: 'OPEN' | 'APPLIED'; model: string; cost: number; messages: number; createdAt: string }
interface Info { businessName: string; models: { model: string; provider: string }[]; defaultModel: string | null; latest: Session | null; sessions: SessionRow[] }
interface ApplyResult { session: Session; applied: string[]; skipped: { part: string; reason: string }[] }

const DAYS = ['1', '2', '3', '4', '5', '6', '0'];

/** Assistente de configuração da IA (super admin): chat com a IA, proposta campo a campo e aplicação por parte. */
export function ConfigAssistantPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const info = useAdminQuery<Info>(`/admin/businesses/${id}/config-assistant`);
  const [session, setSession] = useState<Session | null>(null);
  const [scopes, setScopes] = useState<Scope[]>(['ai', 'scheduling']);
  const [model, setModel] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<null | 'start' | 'send' | 'apply'>(null);
  const [parts, setParts] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<{ part: string; reason: string }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (info.data && !model) setModel(info.data.defaultModel ?? info.data.models[0]?.model ?? ''); }, [info.data, model]);
  useEffect(() => { if (info.data?.latest && !session && info.data.latest.status === 'OPEN') setSession(info.data.latest); }, [info.data, session]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [session?.messages.length, busy]);
  // Só reinicializa a seleção quando o conjunto de partes propostas muda de fato; respostas novas não desfazem o que o super admin desmarcou.
  const partsKey = useRef<string | null>(null);
  useEffect(() => {
    if (!session?.proposal) { partsKey.current = null; return; }
    const next = proposalParts(session.proposal);
    const k = `${session.id}:${next.join(',')}`;
    if (partsKey.current === k) return;
    partsKey.current = k;
    setParts(next);
  }, [session?.id, session?.proposal]);

  const call = async <T,>(kind: 'start' | 'send' | 'apply', fn: () => Promise<T>): Promise<T | null> => {
    setBusy(kind);
    try { return await fn(); } catch (err) { toast.push(err instanceof ApiError ? err.message : t('common:errors.INTERNAL'), 'danger'); return null; } finally { setBusy(null); }
  };
  const start = async () => {
    const s = await call('start', () => api<Session>(`/admin/businesses/${id}/config-assistant/sessions`, { body: { scopes, model } }));
    if (s) { setSession(s); setSkipped([]); }
  };
  const send = async () => {
    if (!session || !text.trim()) return;
    const msg = text.trim(); setText('');
    setSession({ ...session, messages: [...session.messages, { role: 'user', text: msg, at: new Date().toISOString() }] });
    const s = await call('send', () => api<Session>(`/admin/config-assistant/sessions/${session.id}/messages`, { body: { text: msg } }));
    if (s) setSession(s); else setText(msg);
  };
  const apply = async () => {
    if (!session || !parts.length || !confirm(t('admin:assistant.applyConfirm', { name: info.data?.businessName ?? '' }))) return;
    const r = await call('apply', () => api<ApplyResult>(`/admin/config-assistant/sessions/${session.id}/apply`, { body: { parts } }));
    if (r) {
      setSession(r.session); setSkipped(r.skipped);
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === adminKey()[0] });
      toast.push(r.skipped.length ? t('admin:assistant.appliedPartial', { n: r.applied.length }) : t('admin:assistant.applied'), r.skipped.length ? 'warn' : 'success');
    }
  };

  if (info.isLoading) return <><PageHeader title={t('admin:assistant.title')} /><PageLoading cards={2} rows={6} /></>;
  const d = info.data;
  if (!d) return null;
  const header = <PageHeader title={t('admin:assistant.title')} subtitle={t('admin:assistant.subtitle', { name: d.businessName })} actions={<Button onClick={() => navigate(`/negocios/${id}`)}>‹ {d.businessName}</Button>} />;

  if (!session) {
    return (
      <>
        {header}
        <Card title={t('admin:assistant.setupTitle')} subtitle={t('admin:assistant.setupHint')} style={{ maxWidth: 720 }}>
          <div className="k-stack" style={{ gap: 14 }}>
            <div className="k-section-label">{t('admin:assistant.scopes')}</div>
            {SCOPES.map((s) => <Switch key={s} label={t(`admin:assistant.scope.${s}`)} description={t(`admin:assistant.scopeHint.${s}`)} checked={scopes.includes(s)} onChange={(v) => setScopes(v ? [...scopes, s] : scopes.filter((x) => x !== s))} />)}
            <Select label={t('admin:assistant.model')} hint={t('admin:assistant.modelHint')} value={model} onChange={(e) => setModel(e.target.value)}>
              {d.models.map((m) => <option key={m.model} value={m.model}>{m.model}</option>)}
            </Select>
            <Button variant="primary" disabled={!scopes.length || !model} loading={busy === 'start'} onClick={start} style={{ alignSelf: 'flex-start' }}>{t('admin:assistant.start')}</Button>
            {d.latest && <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>{t('admin:assistant.lastSession', { date: new Date(d.latest.createdAt).toLocaleString(i18n.language), status: t(`admin:assistant.status.${d.latest.status}`) })}</p>}
          </div>
        </Card>
        {d.sessions.length > 0 && (
          <Card title={t('admin:assistant.sessionsTitle')} style={{ maxWidth: 720, marginTop: 16 }}>
            <ul className="k-muted" style={{ fontSize: 13, margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
              {d.sessions.map((s) => <li key={s.id}>{t('admin:assistant.sessionLine', { date: new Date(s.createdAt).toLocaleString(i18n.language), origin: t(`admin:assistant.origin.${s.origin}`), status: t(`admin:assistant.status.${s.status}`), messages: s.messages, cost: money(s.cost, i18n.language) })}</li>)}
            </ul>
          </Card>
        )}
      </>
    );
  }

  const p = session.proposal;
  const cur = session.current;
  return (
    <>
      {header}
      <div className="cfa">
        <Card className="cfa__chat" padded={false}>
          <div className="cfa__chat-head">
            <div className="k-row" style={{ gap: 6, flexWrap: 'wrap' }}>{session.scopes.map((s) => <Chip key={s} tone="neutral">{t(`admin:assistant.scopeShort.${s}`)}</Chip>)}<Chip tone="outline">{session.model}</Chip></div>
            <span className="k-muted" style={{ fontSize: 12 }}>{t('admin:assistant.cost', { cost: money(session.cost, i18n.language) })}</span>
          </div>
          <div className="cfa__messages">
            {session.messages.map((m, i) => <div key={i} className={`cfa__msg cfa__msg--${m.role}`}>{m.text}</div>)}
            {busy === 'send' && <div className="cfa__msg cfa__msg--assistant cfa__typing"><span /><span /><span /></div>}
            <div ref={endRef} />
          </div>
          <div className="cfa__compose">
            <Textarea rows={3} value={text} placeholder={session.messages.length <= 1 ? t('admin:assistant.firstPlaceholder') : t('admin:assistant.placeholder')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send(); }} disabled={session.status === 'APPLIED'} />
            <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" variant="ghost" onClick={() => { setSession(null); setSkipped([]); }}>{t('admin:assistant.newSession')}</Button>
              <Button variant="primary" loading={busy === 'send'} disabled={!text.trim() || session.status === 'APPLIED'} onClick={() => void send()}>{t('admin:assistant.send')}</Button>
            </div>
          </div>
        </Card>
        <Card className="cfa__proposal" title={t('admin:assistant.proposal')} subtitle={p ? t('admin:assistant.proposalHint') : t('admin:assistant.noProposal')}>
          {p && (
            <div className="k-stack" style={{ gap: 18 }}>
              {p.ai && <Part id="ai" label={t('admin:assistant.scope.ai')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {(['assistantName', 'tone', 'handoffBehavior'] as const).map((k) => p.ai![k] !== undefined && <Diff key={k} label={t(`admin:assistant.fields.${k}`)} before={cur.ai[k] as string | undefined} after={p.ai![k] as string} />)}
                {p.ai.systemPrompt !== undefined && <Diff label={t('admin:assistant.fields.systemPrompt')} before={cur.ai.systemPrompt} after={p.ai.systemPrompt} long />}
                {(['mustKnow', 'mustNotDo'] as const).map((k) => p.ai![k] !== undefined && <ListDiff key={k} label={t(`admin:assistant.fields.${k}`)} before={cur.ai[k] ?? []} after={p.ai![k]!} />)}
                {p.ai.faq !== undefined && <ListDiff label={t('admin:assistant.fields.faq')} before={(cur.ai.faq ?? []).map((f) => `${f.question}\n${f.answer}`)} after={p.ai.faq.map((f) => `${f.question}\n${f.answer}`)} />}
                {(['appointmentNotesRules', 'outOfHoursMessage', 'contingencyMessage'] as const).map((k) => p.ai![k] !== undefined && <Diff key={k} label={t(`admin:assistant.fields.${k}`)} before={cur.ai[k]} after={p.ai![k]!} long />)}
              </Part>}
              {p.scheduling && <Part id="scheduling" label={t('admin:assistant.scope.scheduling')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {Object.entries(p.scheduling).map(([k, v]) => <Diff key={k} label={t(`admin:assistant.fields.${k}`, { defaultValue: k })} before={fmt(cur.scheduling[k])} after={fmt(v)} />)}
              </Part>}
              {p.services && <Part id="services" label={t('admin:assistant.scope.services')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {p.services.map((s, i) => { const before = s.id ? cur.services.find((x) => x.id === s.id) : undefined; return <Diff key={s.id ?? `new${i}`} label={before ? s.name : `${s.name} · ${t('admin:assistant.newService')}`} before={before ? serviceText(before, i18n.language) : undefined} after={serviceText(s, i18n.language)} long />; })}
              </Part>}
              {p.hours && <Part id="hours" label={t('admin:assistant.scope.hours')} parts={parts} setParts={setParts} applied={session.appliedParts}>
                {DAYS.map((dk) => <Diff key={dk} label={new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(new Date(Date.UTC(2026, 8, 6 + Number(dk), 12)))} before={dayText(cur.hours[dk], t('admin:assistant.closed'))} after={dayText(p.hours![dk], t('admin:assistant.closed'))} />)}
              </Part>}
              {skipped.length > 0 && <div className="cfa__skipped" role="alert">{skipped.map((s) => <div key={s.part}><strong>{t(`admin:assistant.scope.${s.part}`, { defaultValue: s.part })}:</strong> {s.reason}</div>)}</div>}
              <Button variant="primary" disabled={!parts.some((x) => !session.appliedParts.includes(x))} loading={busy === 'apply'} onClick={() => void apply()}>{t('admin:assistant.apply')}</Button>
              <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>{t('admin:assistant.applyHint')}</p>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function proposalParts(p: Proposal): string[] { return (['ai', 'scheduling', 'services', 'hours'] as const).filter((k) => p[k] !== undefined); }
const fmt = (v: unknown) => (v === undefined || v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
function serviceText(s: ServicePart, locale: string): string {
  const price = s.price == null ? '—' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(s.price);
  return [`${s.durationMin ?? '—'} min · ${price} (${s.priceVisibility ?? 'SHOW'}) · folga ${s.bufferBeforeMin ?? 0}/${s.bufferAfterMin ?? 0} min`, s.description ?? ''].filter(Boolean).join('\n');
}
function dayText(d: DayHours | undefined, closed: string): string {
  if (!d || !d.open || !d.intervals.length) return closed;
  return `${d.intervals.map((i) => `${i.start}–${i.end}`).join(', ')}${d.lunch ? ` · almoço ${d.lunch.start}–${d.lunch.end}` : ''}`;
}

function Part({ id, label, parts, setParts, applied, children }: { id: string; label: string; parts: string[]; setParts: (p: string[]) => void; applied: string[]; children: ReactNode }) {
  const { t } = useTranslation(['admin']);
  const done = applied.includes(id);
  return (
    <section className="cfa__part">
      <div className="k-row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <strong>{label}</strong>
        {done ? <Chip tone="accent">{t('admin:assistant.done')}</Chip> : <Switch label={t('admin:assistant.include')} checked={parts.includes(id)} onChange={(v) => setParts(v ? [...parts, id] : parts.filter((x) => x !== id))} />}
      </div>
      <div className="k-stack" style={{ gap: 10, marginTop: 10 }}>{children}</div>
    </section>
  );
}

function Diff({ label, before, after, long }: { label: string; before?: string | null; after: string; long?: boolean }) {
  const { t } = useTranslation(['admin']);
  const same = (before ?? '') === after;
  return (
    <div className={`cfa__diff ${long ? 'cfa__diff--long' : ''}`}>
      <div className="cfa__diff-label">{label}{same && <span className="k-muted"> · {t('admin:assistant.unchanged')}</span>}</div>
      {!same && before !== undefined && before !== null && before !== '' && <div className="cfa__before">{before}</div>}
      <div className={same ? 'cfa__same' : 'cfa__after'}>{after || '—'}</div>
    </div>
  );
}

function ListDiff({ label, before, after }: { label: string; before: string[]; after: string[] }) {
  const { t } = useTranslation(['admin']);
  const removed = before.filter((x) => !after.includes(x));
  return (
    <div className="cfa__diff cfa__diff--long">
      <div className="cfa__diff-label">{label} <span className="k-muted">· {t('admin:assistant.itemsCount', { before: before.length, after: after.length })}</span></div>
      <ul className="cfa__list">
        {after.map((x, i) => <li key={i} className={before.includes(x) ? 'cfa__same' : 'cfa__after'}>{x}</li>)}
        {removed.map((x, i) => <li key={`r${i}`} className="cfa__before">{x}</li>)}
      </ul>
    </div>
  );
}
